import type { SsoSamlConnection } from 'src/app/account/orgSettingsPage/components/SsoWizard/utils';

export const THRESHOLD_FOR_SOON_TO_EXPIRE_MS = 14 * 24 * 60 * 60 * 1000;

export type SamlCertificate =
  SsoSamlConnection['metadata']['verificationCerts'][number];
type CertificateStatus = 'active' | 'expired' | 'expiring soon' | 'upcoming';

export function getCertificateStatus(
  certificate: Pick<SamlCertificate, 'notAfter' | 'notBefore'>,
): CertificateStatus {
  const now = new Date();
  const soonThreshold = new Date(
    now.getTime() + THRESHOLD_FOR_SOON_TO_EXPIRE_MS,
  );

  if (new Date(certificate.notAfter) < now) {
    return 'expired';
  }
  if (new Date(certificate.notBefore) > now) {
    return 'upcoming';
  }

  if (new Date(certificate.notAfter) < soonThreshold) {
    return 'expiring soon';
  }
  return 'active';
}

// A certificate is "active" if it is currently valid (including expiring soon).
export function isActiveCertificate(certificate: SamlCertificate): boolean {
  const status = getCertificateStatus(certificate);
  return status === 'active' || status === 'expiring soon';
}

// Extract the O= (Organization) attribute from the X.509 Subject DN.
// (?:^|,)\s* — match start of string or a comma separator, with optional whitespace
// O=          — the Organization attribute
// ([^,+\\]+)  — capture the value, stopping at attribute separators (, or +) or escape char (\)
// Returns undefined if O= is not present.
export function getCertificateIssuerNameIfExists(
  subjectDN: string,
): string | undefined {
  return subjectDN.match(/(?:^|,)\s*O=([^,+\\]+)/)?.[1]?.trim();
}

export function formatCertificateSummary(
  verificationCerts: SsoSamlConnection['metadata']['verificationCerts'],
): string {
  const segments = [
    {
      label: 'active',
      count: verificationCerts.filter(isActiveCertificate).length,
    },
    {
      label: 'expired',
      count: verificationCerts.filter(
        (cert) => getCertificateStatus(cert) === 'expired',
      ).length,
    },
    {
      label: 'expiring soon',
      count: verificationCerts.filter(
        (cert) => getCertificateStatus(cert) === 'expiring soon',
      ).length,
    },
    {
      label: 'upcoming',
      count: verificationCerts.filter(
        (cert) => getCertificateStatus(cert) === 'upcoming',
      ).length,
    },
  ];

  return segments
    .filter(({ count }) => count > 0)
    .map(({ count, label }) => `${count} ${label}`)
    .join(', ');
}
